import React from 'react';

interface ActionButtonGroupProps {
  children: React.ReactNode;
  /** Status message to display below the button group */
  statusMessage?: string;
  /** Whether the status is in a loading/animating state */
  isLoading?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Whether to reserve space for status area. Set to false when status will never be shown. Default: true */
  reserveStatusSpace?: boolean;
}

/**
 * ActionButtonGroup - Container for action buttons with shared status area
 *
 * This component wraps AIRecommendationsButton and AddManuallyButton (or other buttons)
 * and provides a shared status area below them that spans the full width.
 *
 * Features:
 * - Buttons stay on same line on desktop, can wrap on mobile
 * - Status text appears below ALL buttons, not just one
 * - Consistent spacing and layout
 * - Reserved height prevents layout shift when status appears/disappears
 *
 * Usage:
 * ```tsx
 * <ActionButtonGroup statusMessage={buttonStatus} isLoading={isGenerating}>
 *   <AIRecommendationsButton onClick={...} isLoading={isGenerating} />
 *   <AddManuallyButton onClick={...} />
 * </ActionButtonGroup>
 * ```
 */
export const ActionButtonGroup: React.FC<ActionButtonGroupProps> = ({
  children,
  statusMessage,
  isLoading = false,
  className = '',
  reserveStatusSpace = true,
}) => {
  const showStatusArea = reserveStatusSpace || statusMessage;

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Button row - flex wrap for mobile */}
      <div className="flex flex-wrap items-center gap-4">
        {children}
      </div>
      {/* Shared status area below all buttons - reserved height prevents layout shift */}
      {showStatusArea && (
        <div
          className="min-h-5 mt-1"
          role="status"
          aria-busy={isLoading}
        >
          {statusMessage && (
            <span
              className={`text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap block ${
                isLoading ? 'animate-pulse' : ''
              }`}
              title={statusMessage}
            >
              {statusMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
